/**
 * Slug helpers — keeps numeric IDs out of every public URL.
 *
 * The backend's <c>Course.slug</c> + <c>KBArticle.slug</c> fields are
 * optional. When they are empty, we fall back to a kebab-case version of
 * <c>name</c> / <c>title</c>. The corresponding backend resolver
 * (<c>PublicCoursesController.ResolveCourseBySlugAsync</c>) tries the literal
 * slug first, then the same kebab-cased name as a second pass — so the
 * round-trip works even before slugs are seeded.
 *
 * Ministry has no slug column at all, so its public link is always built
 * from <c>slugify(name)</c>; the ministry detail page resolves it by scanning
 * the cached ministry list client-side.
 */
export function slugify(value: string | null | undefined): string {
  if (!value) return '';
  return value
    .toString()
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '') // strip diacritics
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '') // drop punctuation
    .trim()
    .replace(/\s+/g, '-') // spaces → dashes
    .replace(/-+/g, '-') // collapse dashes
    .replace(/^-|-$/g, ''); // trim leading/trailing dashes
}

/** Build a public URL for a course, preferring the persisted slug. */
export function courseHref(course: { slug?: string | null; name?: string | null }): string {
  const s = (course.slug && course.slug.trim()) || slugify(course.name);
  return `/courses/${encodeURIComponent(s)}`;
}

/** Build a public URL for a ministry — always derived from name (no slug column). */
export function ministryHref(m: { name?: string | null }): string {
  return `/ministries/${encodeURIComponent(slugify(m.name))}`;
}

/** Build a public URL for a KB article. */
export function kbArticleHref(a: { slug?: string | null; title?: string | null }): string {
  const s = (a.slug && a.slug.trim()) || slugify(a.title);
  return `/kb/articles/${encodeURIComponent(s)}`;
}

/** Build a public URL for a KB category (no slug column on the entity). */
export function kbCategoryHref(c: { name?: string | null }): string {
  return `/kb/categories/${encodeURIComponent(slugify(c.name))}`;
}

/** True when a slug from the URL matches an entity's slug or its slugified name. */
export function matchesSlug(
  slug: string | undefined,
  entity: { slug?: string | null; name?: string | null; title?: string | null },
): boolean {
  if (!slug) return false;
  const target = slug.toLowerCase();
  const entitySlug = (entity.slug ?? '').toLowerCase();
  if (entitySlug && entitySlug === target) return true;
  const fallback = slugify(entity.name ?? entity.title);
  return fallback !== '' && fallback === target;
}
